import { useEffect, useRef, useState } from 'react';
import { ChevronsUpDown, Loader2 } from 'lucide-react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/atoms/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/atoms/popover';
import { useEstimateTemplates } from '@/hooks/estimateTemplates/useEstimateTemplates';
import type { EstimateTemplate } from '@/types/estimateTemplate';

/** Shared estimate-template importer used by proposal templates and live proposals. */
export function EstimateTemplateLinePicker({
  onSelect,
  businessUnitId,
}: {
  onSelect: (template: EstimateTemplate) => void;
  /**
   * Narrow to the estimate templates offered to the PARENT document's business
   * unit — the proposal's, or the proposal template's when it has exactly one.
   * There is deliberately no second picker here: the unit was already chosen
   * one level up, and asking again would let the two disagree. Undefined ⇒ no
   * filtering, which is also what a multi-unit or unscoped parent means.
   */
  businessUnitId?: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search]);

  const { data, isLoading } = useEstimateTemplates({
    search: debouncedSearch || undefined,
    businessUnitId,
    limit: 20,
  });
  const templates = data?.data ?? [];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex h-8 w-full items-center justify-between gap-2 rounded-md border bg-background px-3 text-sm text-muted-foreground transition-colors hover:bg-muted/50"
        >
          Select an estimate template to import lines…
          <ChevronsUpDown size={14} className="shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search estimate templates…"
            className="h-8 text-sm"
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {isLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 size={14} className="animate-spin text-muted-foreground" />
              </div>
            ) : null}
            {!isLoading && templates.length === 0 ? (
              <CommandEmpty>No estimate templates found.</CommandEmpty>
            ) : null}
            {!isLoading && templates.length > 0 ? (
              <CommandGroup>
                {templates.map((template) => (
                  <CommandItem
                    key={template.id}
                    value={template.id}
                    onSelect={() => {
                      onSelect(template);
                      setOpen(false);
                      setSearch('');
                    }}
                  >
                    <div className="flex min-w-0 flex-col">
                      <span className="truncate text-sm font-medium">{template.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {template.lineItems.length} item
                        {template.lineItems.length === 1 ? '' : 's'}
                      </span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            ) : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
